import React from "react";
import { Mail, Linkedin, Phone, ShieldCheck, FileLock2, FileDown } from "lucide-react";

const SAMPLE_SOA_URL =
  "https://docs.google.com/document/d/10cmFaykQbqo0TsE2U4chucZM1L1N2Ype/edit?usp=drive_link";
const CALENDLY_URL = "https://calendly.com/hello-bananaparaplanners/30min";

const year = 2026;

const Footer = () => {
  return (
    <footer
      id="footer"
      data-testid="footer-section"
      className="bg-black text-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 bg-[#FACC15] border-2 border-[#FACC15]">
              <span className="text-black font-black text-sm">BP</span>
            </span>
            <span className="font-display font-black text-2xl">
              Banana<span className="text-[#FACC15]">.</span>Paraplanner
            </span>
          </div>
          <p className="mt-4 text-white/75 text-sm leading-relaxed max-w-md">
            Independent contract paraplanning for boutique Australian financial advisors.
            Complex, compliant SOAs delivered directly to you. Engineered for speed, compliance and clarity.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <span className="inline-flex items-center gap-2 border-2 border-[#FACC15] text-[#FACC15] px-3 py-2 text-xs font-bold uppercase tracking-widest">
              <FileLock2 className="w-4 h-4" /> One NDA, whole engagement
            </span>
            <span className="inline-flex items-center gap-2 border-2 border-white text-white px-3 py-2 text-xs font-bold uppercase tracking-widest">
              <ShieldCheck className="w-4 h-4" /> One SLA, whole engagement
            </span>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FACC15]">Quick Links</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="#services" className="hover:text-[#FACC15] transition-colors" data-testid="footer-link-services">Services</a></li>
            <li><a href="#process" className="hover:text-[#FACC15] transition-colors" data-testid="footer-link-process">Process</a></li>
            <li><a href="#pricing" className="hover:text-[#FACC15] transition-colors" data-testid="footer-link-pricing">Pricing</a></li>
            <li><a href="#about" className="hover:text-[#FACC15] transition-colors" data-testid="footer-link-about">About Shanur</a></li>
            <li><a href="#compliance" className="hover:text-[#FACC15] transition-colors" data-testid="footer-link-compliance">Compliance</a></li>
            <li><a href="#booking" className="hover:text-[#FACC15] transition-colors" data-testid="footer-link-booking">Book a Trial SOA</a></li>
            <li>
              <a
                href={SAMPLE_SOA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-[#FACC15] transition-colors"
                data-testid="footer-sample-soa"
              >
                <FileDown className="w-3 h-3" /> Sample SOA
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FACC15]">Contact</p>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <a
                href="mailto:hello@bananaparaplanners.com"
                className="inline-flex items-center gap-2 hover:text-[#FACC15] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" /> hello@bananaparaplanners.com
              </a>
            </li>
            <li>
              <a
                href="tel:+917011921433"
                className="inline-flex items-center gap-2 hover:text-[#FACC15] transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" /> +91 70119 21433
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[#FACC15] transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn / Shanur Rahman
              </a>
            </li>
            <li>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[#FACC15] transition-colors"
                data-testid="footer-calendly"
              >
                📅 Calendly / calendly.com/hello-bananaparaplanners
              </a>
            </li>
          </ul>

          <div className="mt-6 border-2 border-[#FACC15] bg-black p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-[#FACC15]">Engagement Terms</p>
            <p className="text-xs text-white/80 mt-2 leading-relaxed">
              Optional NDA and SLA, at your discretion. If you choose to put paperwork in place,
              one mutual NDA and one SLA at the start cover the entire engagement. Aligned with
              APP 8 and the NDB scheme. Your clients&apos; data, your licensee templates, and your
              IP stay protected by contract.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t-2 border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
          <p data-testid="footer-copyright">
            © {year} Banana Paraplanner. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a href="#privacy" className="hover:text-[#FACC15] transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <a href="#terms" className="hover:text-[#FACC15] transition-colors" data-testid="footer-terms">Terms of Engagement</a>
            <span className="hidden md:inline text-white/30">·</span>
            <span>Built for Australian financial advisers.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
